import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { NewsPostSummary, SchoolEvent } from '@/types'

import Link from 'next/link'
import { ArrowLeft, ArrowRight, CalendarDays } from 'lucide-react'
import { getUpcomingEvents } from '@/lib/content/events'
import { getNewsPage } from '@/lib/content/news'
import { TextLink } from '@/components/generals/action-link'
import { Layout } from '@/components/generals/layout'
import { PageHeader } from '@/components/generals/page-header'
import { Seo } from '@/components/generals/seo'
import { EventList } from '@/components/events/event-list'
import { NewsCard } from '@/components/news/news-card'

import { routes } from '@/constants/navigation'

const PAGE_SIZE = 8

interface NewsIndexProps {
    posts: NewsPostSummary[]
    page: number
    totalPages: number
    events: SchoolEvent[]
}

const pageHref = (page: number): string => (page <= 1 ? routes.news : `${routes.news}?page=${page}`)

const pagerClass = 'inline-flex min-h-11 items-center gap-2 rounded-full border border-border bg-white px-5 font-semibold text-navy transition-colors hover:bg-sand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy'

const NewsIndex: FC<NewsIndexProps> = ({ posts, page, totalPages, events }): ReactNode => {
    return (
        <Layout>
            <Seo
                title={page > 1 ? `News and events, page ${page}` : 'News and events'}
                description='The latest news from Elmsworth Academy, from trips and celebrations to student achievements, plus upcoming events for students and families.'
            />

            <PageHeader
                breadcrumbs={[
                    { label: "Home", href: routes.home },
                    { label: "News & Events" }
                ]}
                title='News & Events'
                intro={<p>Stories from around the school, celebrations of student success and the dates families need to know.</p>}
            />

            <div className='mx-auto grid max-w-7xl gap-12 px-5 py-16 sm:px-6 sm:py-20 lg:grid-cols-3 lg:gap-10'>
                <section aria-labelledby='news-list-heading' className='lg:col-span-2'>
                    <h2 id='news-list-heading' className='font-heading text-2xl font-semibold text-navy'>
                        {page > 1 ? `Latest news, page ${page} of ${totalPages}` : 'Latest news'}
                    </h2>

                    {posts.length > 0 ? (
                        <div className='mt-8 grid gap-6 sm:grid-cols-2'>
                            {posts.map((post) => (
                                <NewsCard key={post.id} post={post} headingLevel='h3' />
                            ))}
                        </div>
                    ) : (
                        <p className='mt-8 rounded-3xl bg-white p-8 text-muted-foreground ring-1 ring-border'>
                            There is no news to show just yet. Please check back soon.
                        </p>
                    )}

                    {totalPages > 1 && (
                        <nav aria-label='News pages' className='mt-12 flex flex-wrap items-center justify-between gap-4'>
                            {page > 1 ? (
                                <Link href={pageHref(page - 1)} className={pagerClass}>
                                    <ArrowLeft aria-hidden='true' className='size-4 text-gold-deep' />
                                    Newer stories
                                </Link>
                            ) : <span />}

                            <p className='text-sm text-muted-foreground'>
                                Page {page} of {totalPages}
                            </p>

                            {page < totalPages ? (
                                <Link href={pageHref(page + 1)} className={pagerClass}>
                                    Older stories
                                    <ArrowRight aria-hidden='true' className='size-4 text-gold-deep' />
                                </Link>
                            ) : <span />}
                        </nav>
                    )}
                </section>

                <aside aria-labelledby='events-list-heading'>
                    <div className='rounded-3xl bg-white p-6 ring-1 ring-border sm:p-8 lg:sticky lg:top-28'>
                        <h2 id='events-list-heading' className='mb-6 flex items-center gap-2 font-heading text-2xl font-semibold text-navy'>
                            <CalendarDays aria-hidden='true' className='size-5 text-gold-deep' />
                            Upcoming events
                        </h2>

                        <EventList events={events} headingLevel='h3' showDescription />

                        <div className='mt-8 flex flex-col gap-3 border-t border-border pt-6'>
                            <TextLink href={routes.calendar}>
                                Full school calendar
                            </TextLink>

                            <TextLink href={routes.termDates}>
                                Term dates
                            </TextLink>
                        </div>
                    </div>
                </aside>
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<NewsIndexProps> = async ({ query }) => {
    const requested = Number.parseInt(typeof query.page === 'string' ? query.page : '1', 10)
    const page = Number.isFinite(requested) && requested > 0 ? requested : 1

    const [newsPage, events] = await Promise.all([
        getNewsPage(page, PAGE_SIZE).catch((error: unknown) => {
            console.error('Could not load news:', error instanceof Error ? error.message : error)
            return { posts: [], page: 1, totalPages: 1 }
        }),
        getUpcomingEvents(6).catch((error: unknown) => {
            console.error('Could not load upcoming events:', error instanceof Error ? error.message : error)
            return []
        })
    ])

    if (page > newsPage.totalPages) return { notFound: true }

    return {
        props: {
            posts: newsPage.posts,
            page: newsPage.page,
            totalPages: newsPage.totalPages,
            events
        }
    }
}

export default NewsIndex
